/**
 * Returns.jsx — EVORA Returns & Exchanges Page
 *
 * AUDIT FIXES:
 * ✅ Full-width desktop layout (no max-width boxing the shell)
 * ✅ True 2-column desktop layout: sticky sidebar + scrollable main
 * ✅ Single H1, strict H2→H3 hierarchy
 * ✅ JSON-LD: WebPage + BreadcrumbList + MerchantReturnPolicy schema
 * ✅ WhatsApp link as <a> (not button + window.open) — better SEO & mobile UX
 * ✅ Eligibility grid — side-by-side desktop, stacked mobile
 * ✅ Refund cards — 3-col on desktop
 * ✅ FAQ zero-JS via <details>
 * ✅ Process uses <ol> (semantic ordered list)
 * ✅ All interactive elements :focus-visible
 * ✅ Skip link, sr-only utility
 * ✅ JSON-LD outside component (no re-serialize on render)
 * ✅ Condition list uses <IconCheck> not <IconPackage> (more meaningful)
 * ✅ CTA section full-width dark band on desktop
 * ✅ Notice styled as <aside role="note"> — correct semantic
 */

import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Layout from '../../components/common/Layout/Layout';
import { BRAND } from '../../config/brand';
import {
  IconReturn,
  IconShield,
  IconAlertCircle,
  IconArrowRight,
  IconWhatsApp,
  IconClock,
  IconCheck,
  IconX,
} from '../../components/common/Icons/Icons';
import './Returns.css';

/* ── Constants ──────────────────────────────────────────────────────────── */
const SITE_URL         = BRAND?.url || process.env.REACT_APP_SITE_URL || 'https://evoraclothing.com';
const BRAND_NAME       = BRAND?.name || 'EVORA';
const RETURNS_URL      = `${SITE_URL}/returns`;
const WHATSAPP_NUMBER  = process.env.REACT_APP_WHATSAPP || '91XXXXXXXXXX';
const WHATSAPP_MESSAGE = encodeURIComponent(
  'Hi, I need help with a return or exchange. My order number is: '
);
const WHATSAPP_HREF = `https://wa.me/${WHATSAPP_NUMBER}?text=${WHATSAPP_MESSAGE}`;

/* ── Accessibility helper ───────────────────────────────────────────────── */
function VisuallyHidden({ children }) {
  return <span className="rp-sr-only">{children}</span>;
}

/* ── WhatsApp CTA (reusable, rendered as <a>) ───────────────────────────── */
function WhatsAppButton({ size = 'md', label = 'Contact on WhatsApp' }) {
  return (
    <a
      href={WHATSAPP_HREF}
      target="_blank"
      rel="noopener noreferrer"
      className={`rp-wa-btn rp-wa-btn--${size}`}
      aria-label="Open WhatsApp to initiate return or exchange"
    >
      <IconWhatsApp size={size === 'lg' ? 22 : 18} aria-hidden="true" />
      {label}
      <VisuallyHidden> (opens WhatsApp)</VisuallyHidden>
    </a>
  );
}

/* ── FAQ data (before JSON-LD so schema can reference it) ────────────────── */
const FAQ = [
  {
    q: 'Can I return an item if I change my mind?',
    a: 'No — returns are accepted only for size issues and defective items, not for change of mind or personal preference.',
  },
  {
    q: 'How do I initiate a return or exchange?',
    a: 'Contact us via WhatsApp within 7 days of delivery with your order number and reason. Our team will guide you step by step.',
  },
  {
    q: 'What if I receive a defective item?',
    a: 'Contact us immediately via WhatsApp with clear photos of the defect. We will arrange a return and provide a replacement or full refund, including return shipping.',
  },
  {
    q: 'Can I exchange for a different size?',
    a: 'Yes — size exchanges are available subject to stock. Contact us within 7 days. If your size is unavailable, a full refund will be issued.',
  },
  {
    q: 'How long does a refund take?',
    a: 'Refunds are credited to the original payment method within 5–7 business days after we receive and inspect the item.',
  },
  {
    q: 'Who pays for return shipping?',
    a: 'We cover return shipping for size issues and defective items. You will receive a prepaid label or step-by-step instructions.',
  },
  {
    q: 'What if I miss the 7-day window?',
    a: 'Please still contact us — we review exceptions for defective items case by case. The sooner you reach out, the better.',
  },
];

/* ── Structured data ────────────────────────────────────────────────────── */
const jsonLd = JSON.stringify({
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'WebPage',
      '@id': `${RETURNS_URL}#webpage`,
      url: RETURNS_URL,
      name: `Returns & Exchanges | ${BRAND_NAME}`,
      description:
        `Returns and exchanges accepted for size issues and defective items only. Contact ${BRAND_NAME} via WhatsApp within 7 days of delivery.`,
      inLanguage: 'en-IN',
      isPartOf: { '@id': `${SITE_URL}/#website` },
      breadcrumb: { '@id': `${RETURNS_URL}#breadcrumb` },
    },
    {
      '@type': 'BreadcrumbList',
      '@id': `${RETURNS_URL}#breadcrumb`,
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_URL}/` },
        { '@type': 'ListItem', position: 2, name: 'Returns & Exchanges', item: RETURNS_URL },
      ],
    },
    {
      '@type': 'MerchantReturnPolicy',
      '@id': `${RETURNS_URL}#policy`,
      name: `${BRAND_NAME} Return Policy`,
      url: RETURNS_URL,
      returnPolicyCategory: 'https://schema.org/MerchantReturnFiniteReturnWindow',
      merchantReturnDays: 7,
      returnMethod: 'https://schema.org/ReturnByMail',
      returnFees: 'https://schema.org/FreeReturn',
      applicableCountry: 'IN',
      description:
        'Returns accepted within 7 days for size issues and defective/incorrect items only. Contact via WhatsApp to initiate.',
    },
    {
      '@type': 'FAQPage',
      '@id': `${RETURNS_URL}#faq`,
      mainEntity: FAQ.map(({ q, a }) => ({
        '@type': 'Question',
        name: q,
        acceptedAnswer: { '@type': 'Answer', text: a },
      })),
    },
  ],
});

/* ── Component ──────────────────────────────────────────────────────────── */
export default function Returns() {
  return (
    <>
      <Helmet>
        <title>Returns &amp; Exchanges | {BRAND_NAME}</title>
        <meta
          name="description"
          content={`${BRAND_NAME} accepts returns and exchanges for size issues and defective items only. Initiate via WhatsApp within 7 days of delivery. Free return shipping provided.`}
        />
        <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large" />
        <link rel="canonical" href={RETURNS_URL} />

        <link rel="prefetch" href="/shipping" as="document" />
        <link rel="prefetch" href="/contact" as="document" />
        <link rel="prefetch" href="/faq" as="document" />

        <meta property="og:type" content="website" />
        <meta property="og:url" content={RETURNS_URL} />
        <meta property="og:site_name" content={BRAND_NAME} />
        <meta property="og:title" content={`Returns & Exchanges | ${BRAND_NAME}`} />
        <meta property="og:description" content="Returns for size issues & defective items only. Initiate via WhatsApp within 7 days. Free return shipping." />
        <meta property="og:image" content={`${SITE_URL}/og-image.jpg`} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content="Returns & Exchanges — Size issues and defective items only" />
        <meta property="og:locale" content="en_IN" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`Returns & Exchanges | ${BRAND_NAME}`} />
        <meta name="twitter:description" content="Returns for size issues & defective items. Contact WhatsApp within 7 days. Free return shipping." />
        <meta name="twitter:image" content={`${SITE_URL}/og-image.jpg`} />
        <meta name="twitter:image:alt" content="Returns & Exchanges — Size issues and defective items only" />

        <script type="application/ld+json">{jsonLd}</script>
      </Helmet>

      <Layout showFooter showBottomTab>
        <main className="rp-page" id="main-content">

          {/* Skip link */}
          <a href="#rp-main" className="rp-skip">Skip to returns policy</a>

          {/* ── Full-width page band ───────────────────────────────────── */}
          <div className="rp-hero">
            <div className="rp-hero__inner">

              {/* Breadcrumb */}
              <nav className="rp-breadcrumb" aria-label="Breadcrumb">
                <ol className="rp-breadcrumb__list">
                  <li><Link to="/" className="rp-breadcrumb__link">Home</Link></li>
                  <li className="rp-breadcrumb__sep" aria-hidden="true" />
                  <li><span aria-current="page" className="rp-breadcrumb__current">Returns &amp; Exchanges</span></li>
                </ol>
              </nav>

              {/* H1 */}
              <h1 className="rp-hero__title">Returns &amp; Exchanges</h1>
              <p className="rp-hero__subtitle">
                We stand behind every piece. Returns and exchanges are available for{' '}
                <strong>size issues</strong> and <strong>defective items</strong> — initiated via WhatsApp within 7 days.
              </p>

              {/* Hero CTA */}
              <WhatsAppButton size="lg" label="Start a Return on WhatsApp" />
            </div>
          </div>

          {/* ── Body: sidebar + main ───────────────────────────────────── */}
          <div className="rp-layout" id="rp-main">

            {/* ── Sticky Sidebar (desktop) ─────────────────────────────── */}
            <aside className="rp-sidebar" aria-label="Policy at a glance">
              <div className="rp-sidebar__inner">
                <p className="rp-sidebar__label">At a Glance</p>

                {[
                  { icon: <IconWhatsApp size={20} />,    title: 'WhatsApp Only',     text: 'All requests via WhatsApp' },
                  { icon: <IconClock size={20} />,       title: '7-Day Window',      text: 'Contact within 7 days of delivery' },
                  { icon: <IconReturn size={20} />,      title: 'Eligible Cases',    text: 'Size issues & defective items' },
                  { icon: <IconShield size={20} />,      title: 'Free Return Ship',  text: 'We cover return shipping costs' },
                ].map(({ icon, title, text }) => (
                  <div key={title} className="rp-sidebar__item">
                    <span className="rp-sidebar__item-icon" aria-hidden="true">{icon}</span>
                    <div>
                      <p className="rp-sidebar__item-title">{title}</p>
                      <p className="rp-sidebar__item-text">{text}</p>
                    </div>
                  </div>
                ))}

                <div className="rp-sidebar__divider" />
                <WhatsAppButton size="sm" label="Contact on WhatsApp" />
                <p className="rp-sidebar__hours">Mon–Sat · 10 AM – 7 PM IST</p>
              </div>
            </aside>

            {/* ── Main content column ──────────────────────────────────── */}
            <div className="rp-main">

              {/* ── Policy notice ─────────────────────────────────────── */}
              <aside className="rp-notice" role="note" aria-label="Important policy notice">
                <IconAlertCircle size={20} aria-hidden="true" className="rp-notice__icon" />
                <div className="rp-notice__body">
                  <strong>No general returns or exchanges.</strong> We accept returns only for size issues and defective / incorrect items. All other requests will not be processed.
                </div>
              </aside>

              {/* ── Eligibility ───────────────────────────────────────── */}
              <section className="rp-section" aria-labelledby="elig-h">
                <h2 id="elig-h" className="rp-section__title">Eligibility Criteria</h2>
                <div className="rp-eligibility">
                  <div className="rp-elig rp-elig--yes">
                    <div className="rp-elig__header">
                      <IconCheck size={18} aria-hidden="true" />
                      <h3 className="rp-elig__title">Eligible</h3>
                    </div>
                    <ul className="rp-elig__list">
                      <li>Size doesn't fit (too small or too large)</li>
                      <li>Manufacturing defects — stitching, fabric</li>
                      <li>Item damaged on arrival</li>
                      <li>Wrong item received</li>
                    </ul>
                  </div>
                  <div className="rp-elig rp-elig--no">
                    <div className="rp-elig__header">
                      <IconX size={18} aria-hidden="true" />
                      <h3 className="rp-elig__title">Not Eligible</h3>
                    </div>
                    <ul className="rp-elig__list">
                      <li>Change of mind or personal preference</li>
                      <li>Items worn, washed, or altered</li>
                      <li>Missing original tags or packaging</li>
                      <li>Returned after 7 days of delivery</li>
                      <li>Sale items (unless defective)</li>
                    </ul>
                  </div>
                </div>
              </section>

              {/* ── Condition requirements ────────────────────────────── */}
              <section className="rp-section" aria-labelledby="cond-h">
                <h2 id="cond-h" className="rp-section__title">Item Condition Requirements</h2>
                <p className="rp-text">Items must meet all of the following to be accepted:</p>
                <ul className="rp-checklist">
                  {[
                    'Unworn and unwashed',
                    'All original tags attached',
                    'Original packaging included (if available)',
                    'No signs of use, damage, or alteration',
                  ].map((item) => (
                    <li key={item} className="rp-checklist__item">
                      <IconCheck size={14} aria-hidden="true" className="rp-checklist__icon" />
                      {item}
                    </li>
                  ))}
                </ul>
                <div className="rp-warning" role="note">
                  <IconAlertCircle size={16} aria-hidden="true" />
                  <p>Items failing these conditions will be rejected and returned at your expense.</p>
                </div>
              </section>

              {/* ── Returns process ───────────────────────────────────── */}
              <section className="rp-section" aria-labelledby="process-h">
                <h2 id="process-h" className="rp-section__title">How to Return or Exchange</h2>
                <ol className="rp-steps" aria-label="Return process steps">
                  {[
                    {
                      title: 'Contact via WhatsApp',
                      text:  'Message us within 7 days of delivery with your order number and reason (size issue or defective item).',
                      cta:   true,
                    },
                    {
                      title: 'Team Review',
                      text:  'We verify your request. For defective items, we may ask for photos of the damage.',
                    },
                    {
                      title: 'Return Authorization',
                      text:  'If approved, we provide a return authorization and shipping instructions. Do not return without authorization.',
                    },
                    {
                      title: 'Ship the Item',
                      text:  'Pack securely with all tags and original packaging. Ship to the address we provide.',
                    },
                    {
                      title: 'Refund or Exchange Processed',
                      text:  'Once received and inspected, refunds go to your original payment method within 5–7 business days.',
                    },
                  ].map(({ title, text, cta }, i) => (
                    <li key={title} className="rp-step">
                      <div className="rp-step__badge" aria-hidden="true">{i + 1}</div>
                      <div className="rp-step__body">
                        <h3 className="rp-step__title">{title}</h3>
                        <p className="rp-step__text">{text}</p>
                        {cta && <WhatsAppButton size="sm" label="Message on WhatsApp" />}
                      </div>
                    </li>
                  ))}
                </ol>
              </section>

              {/* ── Refund & exchange policy ──────────────────────────── */}
              <section className="rp-section" aria-labelledby="refund-h">
                <h2 id="refund-h" className="rp-section__title">Refund &amp; Exchange Details</h2>
                <div className="rp-refund-grid">
                  {[
                    {
                      title: 'Refunds',
                      text:  'Processed to original payment within 5–7 business days. Shipping fees are non-refundable unless we sent the wrong or defective item.',
                    },
                    {
                      title: 'Exchanges',
                      text:  'Available for size issues, subject to stock. If your size is out of stock, a full refund is issued automatically.',
                    },
                    {
                      title: 'Return Shipping',
                      text:  'We cover return shipping for size issues and defective items — prepaid label or step-by-step instructions provided.',
                    },
                  ].map(({ title, text }) => (
                    <div key={title} className="rp-refund-card">
                      <h3 className="rp-refund-card__title">{title}</h3>
                      <p className="rp-refund-card__text">{text}</p>
                    </div>
                  ))}
                </div>
              </section>

              {/* ── FAQ ───────────────────────────────────────────────── */}
              <section className="rp-section" aria-labelledby="faq-h">
                <h2 id="faq-h" className="rp-section__title">Frequently Asked Questions</h2>
                <div className="rp-faq">
                  {FAQ.map(({ q, a }) => (
                    <details key={q} className="rp-faq__item">
                      <summary className="rp-faq__q">
                        <span>{q}</span>
                        <span className="rp-faq__chevron" aria-hidden="true" />
                      </summary>
                      <div className="rp-faq__a"><p>{a}</p></div>
                    </details>
                  ))}
                </div>
              </section>

            </div>{/* end rp-main */}
          </div>{/* end rp-layout */}

          {/* ── Related (internal linking) ──────────────────────────────── */}
          <nav className="rp-related" aria-label="Related information">
            <h2 className="rp-related__title">Related information</h2>
            <ul className="rp-related__list" role="list">
              <li><Link to="/shipping" className="rp-related__link">Shipping &amp; Delivery</Link></li>
              <li><Link to="/contact" className="rp-related__link">Contact Us</Link></li>
              <li><Link to="/faq" className="rp-related__link">FAQ</Link></li>
              <li><Link to="/shop" className="rp-related__link">Shop All</Link></li>
            </ul>
          </nav>

          {/* ── Full-width CTA band ────────────────────────────────────── */}
          <div className="rp-cta-band">
            <div className="rp-cta-band__inner">
              <div className="rp-cta-band__text">
                <p className="rp-cta-band__eyebrow">Need help?</p>
                <h2 className="rp-cta-band__title">Start Your Return or Exchange</h2>
                <p className="rp-cta-band__sub">Available Monday–Saturday · 10 AM – 7 PM IST</p>
              </div>
              <WhatsAppButton size="lg" label="Contact on WhatsApp" />
            </div>
          </div>

        </main>
      </Layout>
    </>
  );
}